<template>
  <div
    :style="{ paddingBottom: !showTotal ? `${padding}px` : 'none' }"
    :class="{
      'extend-height': extendHeight,
      'table-actions': actions,
      'fit-content': !heightProp
    }"
    class="ui-table"
  >
    <ElTable
      ref="dataTable"
      :data="data"
      :default-sort="getDefaultSort"
      :height="computedHeight"
      :max-height="heightProp"
      :show-header="showHeader"
      :show-summary="showTotal"
      :summary-method="getSummaryRow"
      :highlight-current-row="rowSelectable"
      :row-class-name="rowCheck ? computedRowClasss : computedRowClass"
      :tooltip-options="overflowTooltipSettings"
      :tooltip-formatter="tooltipFormatter"
      border
      @sort-change="handleSort"
      @expand-change="handleExpand"
      @current-change="handleCurrentRowChange"
      @row-click="handleCurrentRowClick"
      @row-contextmenu="contextMenu"
      @header-dragend="handleResize"
    >
      <slot
        :render-header="renderHeader"
        :format-value="formatValue"
        name="columns"
      >
        <ElTableColumn
          v-for="(field, index) in computedColumns"
          :key="index"
          :prop="field.name"
          :label="field.title ? field.title : $t(`${i18nPath}.fields.${field.name}.title`) || ''"
          :align="field.align || 'left'"
          :header-align="field.align || 'left'"
          :min-width="field.minWidth || 'auto'"
          :width="field.width || 'auto'"
          :sortable="disableSort || customSorting ? false : field.sortable || false"
          :formatter="field.formatter"
          :currency="field.currency"
          :show-overflow-tooltip="!field.customTooltip"
          :sort-orders="sortOrders"
          resizable
        >
          <template v-slot:header="slotProps">
            <Vnodes :vnodes="renderHeader(slotProps)" />
          </template>

          <template v-slot="scope">
            <div
              v-if="field.link"
              :class="[field.class, field.computedClass ? field.computedClass(scope.row[field.name], scope.row) : '']"
              class="row-value link"
            >
              <RouterLink :to="field.link(scope.row)">
                {{ scope.row[field.name] }}
              </RouterLink>
            </div>
            <Vnodes
              v-else-if="field.linkAppend"
              :vnodes="field.linkAppend(scope.row)"
            />
            <div
              v-else-if="field.renderValue && !field.customTooltip"
              :class="[field.class, field.computedClass ? field.computedClass(scope.row[field.name], scope.row) : '']"
              class="row-value"
              v-html="field.renderValue(scope.row[field.name], scope.row, field)"
            />
            <div
              v-else-if="field.renderValue && field.customTooltip"
              :class="[field.class, field.computedClass ? field.computedClass(scope.row[field.name], scope.row) : '']"
              class="row-value"
            >
              <span
                class="length-up !tw-whitespace-nowrap !tw-flex !tw-justify-between"
                :class="{'with-badge': scope.row[field.name].count,}"
              >
                <ElTooltip
                  v-if="tooltipExist[scope.row.id]"
                  :content="scope.row[field.name].text"
                  placement="top"
                  popper-class="ui-table__popper ui-table__hide-popper"
                  :show-after="500"
                  :persistent="false"
                >
                  <span v-html="field.renderValue(scope.row[field.name], scope.row, field)" />
                </ElTooltip>
                <span
                  v-if="scope.row[field.name].count"
                  class="tab-count mt-2"
                >{{ scope.row[field.name].count }}</span>
              </span>
            </div>
            <div
              v-else-if="field.copyable"
            >
              <span>{{ scope.row[field.name] }}
                <ElTooltip
                  v-if="tooltipExist[scope.row.id]"
                  placement="right"
                  popper-class="right"
                  :disabled="copied !== scope.row[field.name]"
                  :value="copied !== scope.row[field.name]"
                  :content="$t('mediaBuying.promocodes.copy_success_message')"
                  :persistent="false"
                >
                  <i
                    class="far fa-copy text-green"
                    @click="() => copyToClipboard(scope.row[field.name])"
                  />
                </ElTooltip>
              </span>
            </div>
            <div
              v-else
              :ref="field.customTooltip && field.whiteSpacedText && scope.row['id']"
              :class="[field.class, field.computedClass ? field.computedClass(scope.row[field.name], scope.row) : '']"
              class="row-value"
              @click="field.click ? field.click(scope.row) : ''"
            >
              <span v-if="field.computedValue">{{ field.computedValue(scope.row, scope.row[field.name]) }}</span>
              <span v-if="field.customTooltip && !field.whiteSpacedText">
                <span
                  v-if="field.renderTooltip(scope.row[field.name]).length >= 1 &&
                    field.renderTooltip(scope.row[field.name])[0] !== ''"
                  class="length-up"
                >
                  <ElTooltip
                    v-if="tooltipExist[scope.row.id]"
                    placement="top"
                    popper-class="urls-tooltip"
                    :show-after="500"
                    :persistent="false"
                  >
                    <span>
                      <a
                        :href="field.renderTooltip(scope.row[field.name])[0]"
                        target="_blank"
                        class="text-green"
                      >
                        {{ field.renderTooltip(scope.row[field.name])[0] }}
                      </a>
                      <span
                        v-if="field.renderTooltip(scope.row[field.name]).length > 1"
                        class="countUrl"
                      >
                        +{{ field.renderTooltip(scope.row[field.name]).length - 1 }}
                      </span>
                    </span>
                    <template v-slot:content>
                      <div>
                        <span
                          v-for="(url, index) in field.renderTooltip(scope.row[field.name])"
                          :key="index"
                          class="!tw-flex !tw-flex-col"
                        >
                          <a
                            :href="url"
                            target="_blank"
                          >{{ url }}</a>
                        </span>
                      </div>
                    </template>
                  </ElTooltip>
                </span>
                <span v-else>—</span>
              </span>
              <span
                v-else-if="field.customTooltip && field.whiteSpacedText"
                class="length-up !tw-whitespace-nowrap"
              >
                <ElTooltip
                  v-if="tooltipExist[scope.row.id]"
                  :placement="placementTooltip || 'top'"
                  :show-after="500"
                  :persistent="false"
                >
                  <template v-slot:content>
                    <div>{{ scope.row[field.name] }}</div>
                  </template>
                  <span class="overflow-ellipsis">{{ scope.row[field.name] }}</span>
                </ElTooltip>
              </span>
              <span v-else-if="field.component">
                <RenderComponent :data="field.component(scope.row, scope.row[field.name])" />
              </span>
              <span v-else-if="field.format">
                {{ formatValue(scope.row[field.name], field.format, field.formatOptions, scope.row) }}
              </span>
              <span v-else>
                {{ scope.row[field.name] === null ? '—' : scope.row[field.name] }}
              </span>
              <!-- <Flag
                v-if="field.format === 'ip-flag'"
                :iso="scope.row.country_code"
                :title="scope.row.country_name"
              /> -->
            </div>
          </template>
        </ElTableColumn>
        <ElTableColumn
          v-if="actions"
          :label="actions.name"
          :align="actions.align || 'center'"
          :width="actions.width || 'auto'"
          :fixed="actions.fixed || false"
          property="$utility-actions"
          :resizable="false"
        >
          <template v-slot="scope">
            <div class="row-value actions">
              <template
                v-for="(action, i) in actions.operations"
                :key="i"
              >
                <span
                  v-if="action.computedValue"
                  :key="`computed-${action.name}`"
                  :class="action.class"
                  v-html="action.computedValue(scope.row)"
                />
                <RenderComponent 
                  v-else-if="action.component" 
                  :key="`component-${action.name}`"
                  :data="action.component(scope.row, target)"
                />
                <ElPopover
                  v-else-if="action.popup"
                  :key="`popup-${action.name}`"
                  ref="popover"
                  placement="top-end"
                  trigger="click"
                >
                  <div class="message__wrapper">
                    <p>{{ $t('topNavbar.wantDelete') }}?</p>
                    <div class="!tw-flex !tw-justify-end">
                      <button
                        class="btn btn-outline-primary btn-sm mb-0"
                        @click="hidePopup()"
                      >
                        {{ $t('alerts.dataTable.cancel') }}
                      </button>
                      <button
                        class="btn btn-danger btn-sm mb-0 ml-8"
                        @click="action.callback(scope.row, action.callbackOptions, $event), hidePopup()"
                      >
                        {{ $t('alerts.dataTable.delete') }}
                      </button>
                    </div>
                  </div>
                  <template v-slot:reference>
                    <span 
                      v-if="action.icon"
                      :class="`${action.class} el-tooltip icon__wrapper ${action.status !== scope.row.status ? 'disabled' : ''}`"
                    >
                      <i
                        v-if="action.icon"
                        :class="`far fa-${action.icon}`"
                      />
                    </span>
                    <span
                      v-else
                      :class="action.class"
                    >
                      {{ action.name }}
                    </span>
                  </template>
                </ElPopover>
                <ElTooltip
                  v-else-if="action.icon"
                  :key="`action-${action.name}`"
                  placement="top"
                  popper-class="tooltip-icon"
                  :show-after="500"
                  :persistent="false"
                >
                  <a
                    class="icon__wrapper"
                    :class="action.class"
                    :href="action.url ? action.url(scope.row, action.callbackOptions) : null"
                    @click.stop="action.callback(scope.row)"
                  >
                    <i :class="`far fa-${action.icon}`" />
                  </a>
                  <template v-slot:content>
                    <div>
                      {{ action.tooltip }}
                    </div>
                  </template>
                </ElTooltip>
                <template v-else-if="action.componentAlt">
                  <component 
                    :is="action.componentAlt.component"
                    v-if="!action.visible || action.visible(scope.row)"
                    :key="`component-alt-${action.name}`"
                    v-bind="action.componentAlt.props(scope.row)"
                    :row="scope.row"
                    :is-mobile="false"
                  />
                </template>
                <a
                  v-else
                  :key="`action-alt-${action.name}`"
                  :class="action.class"
                  :href="action.url ? action.url(scope.row, action.callbackOptions) : null"
                  @click="action.callback(scope.row, action.callbackOptions, $event)"
                >{{ action.name }}</a>
                <span class="divider" />
              </template>
            </div>
          </template>
        </ElTableColumn>
      </slot>
      <template
        v-if="!dontFillEmpty"
        v-slot:append
      >
        <table
          ref="emptySpace"
          class="el-table__body ui-table__fill-empty"
          cellspacing="0"
          cellpadding="0"
          border="0"
          style="width:100%"
        >
          <tbody>
            <tr style="height:0;">
              <td
                v-for="field in computedColumnsFooter"
                :key="field.name"
              />
            </tr>
          </tbody>
        </table>
      </template>
    </ElTable>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { h, resolveComponent } from 'vue';
import DimensionsMixin from '@/components/Report/Components/DimensionsMixin';
import copyMixin from '@/mixins/copy';
import render from './render';
import VNode from './VNode.vue';

import Popover from '../../Popover';
export default {
  name: 'UiTable',
  components: {
    RenderComponent: render,
    'ACPopover': Popover,
    Vnodes: VNode,
  },
  mixins: [DimensionsMixin, copyMixin],
  props: {
    hideFields: {
      type: Array,
      default() {
        return [];
      },
    },
    fields: {
      type: Array,
      default() {
        return [];
      },
    },
    data: {
      type: Array,
      default() {
        return [];
      },
    },
    showTotal: {
      type: Boolean,
      default: false,
    },
    rowCheck: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Object,
      default() {
        return {};
      },
    },
    customTotalRow: {
      type: Array,
      default() {
        return [];
      },
    },
    sort: {
      type: Object,
      default() {
        return {};
      },
    },
    i18nPath: {
      type: String,
      default: '',
    },
    minTableHeight: {
      type: Number,
      default: 400,
    },
    minCellWidth: {
      type: Number,
      default: 50,
    },
    padding: {
      type: Number,
      default: 40,
    },
    rowsCount: {
      type: Number,
      default: 0,
    },
    isStatic: {
      type: Boolean,
      default: false,
    },
    dontFillEmpty: {
      type: Boolean,
      default: false,
    },
    disableSort: {
      type: Boolean,
      default: false,
    },
    externalHeight: {
      type: [Number, String],
      default: '',
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    actions: {
      type: [Object, Array],
      default: null,
    },
    rowClick: {
      type: Function,
      default: () => {
      },
    },
    rowSelectable: {
      type: Boolean,
      default: false,
    },
    colFilters: {
      type: Object,
      default: () => {
      },
    },
    customContext: {
      type: Boolean,
      default: false,
    },
    computedRowClass: {
      type: Function,
      default() {
        return '';
      },
    },
    extendHeight: {
      type: Boolean,
      default: false,
    },
    customSorting: {
      type: Boolean,
      default: false,
    },
    checkedRow: {
      type: Boolean,
      default: false,
    },
    checkedRowId: {
      type: String,
      default: 'id',
    },
    selectRow: {
      type: Boolean,
      default: false,
    },
    placementTooltip: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: '',
    },
    tooltipFormatter: {
      type: Function,
    },
    isRowClickable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['init', 'context', 'current-change', 'expand-change', 'filter', 'sort', 'change-rows-count', 'checkedRows'],
  data() {
    return {
      lastSortProp: null,
      lastSortOrder: null,
      tableHeight: 0,
      tableStatus: {
        height: false,
        rows: false,
      },
      sortProp: '',
      sortOrder: '',
      columnFilters: {},
      tooltipExist: {},
      lastOpenFilter: '',
      isFiltersInit: false,
      pCurrentRow: {},
      checkApplyFilter: false,
      activeFilter: '',
      activeColumnFilters: {},
      checkAll: false,
      checkRows: [],
      isPressCtrl: false,
      copied: null,
      amount: '',
      ready: false,
      heightPropLazy: 0,
      pPadding: 0,
    };
  },
  computed: {
    computedColumnsFooter() {
      return this.$_.filter(this.fields, (f) => this.$_.isUndefined(f.show) ? true : f.show);
    },
    computedColumns() {
      return this.$_.filter(
        this.fields, 
        (f) => (this.$_.isUndefined(f.show) && !this.hideFields.includes(f.name)) || f.show,
      );
    },  
    currentRow: {
      get() {
        return this.pCurrentRow; 
      },
      set(value) {
        this.pCurrentRow = value.value ? this.$_.cloneDeep(value.value) : {};
      },
    },
    target() {
      return this;
    },
    tableReady() {
      return this.tableStatus.height && this.tableStatus.rows;
    },
    getDefaultSort() {
      return {
        prop: this.sortProp,
        order: `${this.sortOrder}ending`,
      };
    },
    heightProp() {
      // change detections issue in chrome
      const externalHeight = this.externalHeight;
      const tableHeight = this.tableHeight;

      return this.isStatic
        ? externalHeight || undefined
        : tableHeight || undefined;
    },
    // vue3-migration -- отсутствующие геттеры в сторе, добавил заглушки
    ...mapGetters({
      countries: 'misc/countries',
      // groups: 'misc/groups',
    }),
    groups() {
      return [];
    },
    
    overflowTooltipSettings() {
      return { 
        enterable: true,
        placement: 'top', 
        showArrow: true,
        hideAfter: 200,
        teleport: true,
        popperOptions: { 
          strategy: 'fixed', 
        },
        popperClass: 'ui-table__popper ui-table__hide-popper',
        showAfter: 500,
      };
    },
    computedHeight() {
      return !this.extendHeight && this.heightProp 
        ? this.heightProp 
        : undefined;
    },
    isSortDesc() {
      return this.sort && this.sort.order === 'desc';
    },
    sortOrders(column) {
      return this.getSortOrders(this.isSortDesc);
    },
  },
  watch: {
    data: {
      deep: true,
      immediate: true,
      handler(value) {
        this.tooltipExist = {};
        value.forEach((item) => {
          this.tooltipExist[item.id] = true;
        });
      },
    },
    isSortDesc: {
      handler(value) {
        if(this.$refs?.dataTable?.columns) {
          const sortOrders = this.getSortOrders(value);
          this.$refs.dataTable.columns
            .filter((column) => column.sortable)
            .forEach((column) => {
              column.sortOrders = sortOrders;
            });
        }
      },
    },
    lastOpenFilter(v) {
      if (v === '') {
        Array.from(document.querySelectorAll('.cell.hover-once'))
          .map((e) => e.classList.remove('hover-once'));
        if (!this.checkApplyFilter) {
          this.columnFilters[this.activeFilter] = this.activeColumnFilters[this.activeFilter];
          this.columnFilters[this.activeFilter].visible = false;
        }
      } else {
        this.activeFilter = v;
        this.activeColumnFilters = this.$_.cloneDeep(this.columnFilters);
      }
    },
    sort: {
      deep: true,
      handler(nv) {
        if (this.customSorting) {
          this.sortProp = nv.prop;
          this.sortOrder = nv.order;
        } else {
          this.sortProp = nv.prop;
          this.sortOrder = nv.order;
          this.$refs.dataTable.sort(this.sortProp, `${this.sortOrder}ending`);
          this.$refs.dataTable.doLayout();
          this.$nextTick(() => {
            this.$refs.dataTable.sort(this.sortProp, `${this.sortOrder}ending`);
            this.$refs.dataTable.doLayout();
          });
        }
      },
    },
    fields: {
      deep: true,
      handler() {
        this.isFiltersInit = false;
        this.fields.forEach((field) => {
          if (
            field.filterable 
              && (this.$_.isEmpty(this.columnFilters[field.name]) || !this.columnFilters[field.name].op)
          ) {
            this.$set(this.columnFilters, field.name, {
              visible: false,
              op: '',
              value: '',
            });
          }
        });
        this.$nextTick(() => {
          // vue3-migration -- forceUpdate
          // this.$refs.dataTable.$forceUpdate();
        });
        this.isFiltersInit = true;
      },
    },
    colFilters: {
      deep: true,
      handler(nv) {
        this.setColumnFilters(nv);
      },
    },
  },
  methods: {
    handleResize(newWidth, oldWidth, column, event) {
      if (newWidth < this.minCellWidth) {
        column.width = this.minCellWidth;
      }
    },
    async handleCurrentRowClick(row, e) {
      // блокируем вызов выбора строки если нажали на колонку actions
      if (!e || e.property === '$utility-actions') {
        return;
      }
      
      if (!this.selectRow) {
        if (!this.currentRow || this.currentRow.row_id !== row.row_id) {
          if (this.checkRows.length >= 1) {
            this.checkRows = [];
          }

          if (this.checkRows.indexOf(row.row_id) === -1) {
            this.checkRows.push(row.row_id);
            this.$emit('checkedRows', row.row_id, this.checkRows, row);
          }
        } else {
          this.checkRows = [];
          this.$emit('checkedRows', row.row_id, this.checkRows, row);
        }
      }

      this.rowClick(row);

      if (row === this.currentRow) {
        this.$refs.dataTable.setCurrentRow();
      }

      await this.clearTooltips(row);

      this.$nextTick(() => {
        if (this.$refs.dataTable) {
          const { currentRow } = this.$refs.dataTable.store.states;
          this.currentRow = currentRow;
        }
      });

      if (this.selectRow) {
        e && e.preventDefault && e.preventDefault();
        e && e.stopPropagation && e.stopPropagation();
        this.changeRowCheck(row);
      }
    },
    clearTooltips(row) {
      const allPoppers = document.querySelectorAll('.ui-table__hide-popper');
      allPoppers.forEach((popper) => {
        popper.style.display = 'none';
      });

      if(!row) return;

      if(this.isRowClickable) {
        this.tooltipExist[row.id] = false;
      }

      this.$nextTick(() => {
        if (this.$refs.dataTable) {
          this.tooltipExist[row.id] = true;
        }
      });
    },

    computedRowClasss({ row }) {
      if (this.currentRow && (row.row_id === this.currentRow.row_id)) {
        if (this.checkRows.indexOf(row.row_id) === -1 && this.checkRows.length) {
          return '';
        } else {
          return 'current-row';
        }
      } else if (this.checkRows.indexOf(row.row_id) !== -1) {
        return 'current-row';
      } else {
        return '';
      }
    },
    changeRowCheck(row) {
      if (this.checkRows.indexOf(row.row_id) === -1) {
        this.checkRows.push(row.row_id);
      } else {
        this.checkRows = this.checkRows.filter((item) => item !== row.row_id);
      }
      this.$emit('checkedRows', row.row_id, this.checkRows, row);
    },
    clearCheck() {
      this.checkRows = [];
      this.currentRow = {};
    },
    hidePopup() {
      this.$refs.popover.forEach((e) => e.hide());
    },
    fillEmptySpace() {
      if (this.dontFillEmpty) {
        return;
      }

      try {
        const { dataTable, emptySpace } = this.$refs;
        emptySpace.style.width = `${dataTable.$el.querySelector('table').offsetWidth}px`;
        const colgroup = emptySpace.querySelector('colgroup');
        if (colgroup) {
          colgroup.remove();
        }
        emptySpace.appendChild(dataTable.$el.querySelector('table colgroup')
          .cloneNode(true));

        const tr = emptySpace.querySelector('tr');
        tr.style.display = 'table-row';
        const originRows = dataTable.$el.querySelectorAll('table tbody .el-table__row ');
        let height = dataTable.$el.querySelector('.el-table__body-wrapper').offsetHeight;

        if (originRows) {
          height -= (originRows.length * originRows[0].offsetHeight);
          height = height < 0 ? 0 : height;
        }

        tr.style.height = `${height}px`;

        if (!height) {
          tr.style.display = 'none';
        }
      } catch {
      }
    },
    setColumnFilters(filters) {
      this.isFiltersInit = false;
      this.columnFilters = {};
      this.fields.forEach((field) => {
        if (field.filterable) {
          this.$set(this.columnFilters, field.name, {
            visible: false,
            op: '',
            value: '',
          });
        }
      });
      if (filters) {
        this.columnFilters = this.$_.assign({}, this.columnFilters, filters);
      }
      this.isFiltersInit = true;
    },
    computeTableHeight() {
      let th = window.innerHeight - this.$el.offsetTop;

      if (!this.showTotal) {
        th -= this.pPadding;
      }

      if (th !== this.tableHeight) {
        // deactivated recalc issue fix
        if (this.$el.offsetTop || !this.tableHeight) {
          this.tableHeight = th < this.minTableHeight ? this.minTableHeight : th;
        }

        this.tableStatus.height = true;
        this.tableStatus.rows = false;
      }

      this.fillEmptySpace();
    },
    computeRowsCount() {
      const theader = this.$refs.dataTable.$el.querySelector('.el-table__header-wrapper');
      const tbody = this.$refs.dataTable.$el.querySelector('.el-table__body-wrapper');
      const ttotal = this.$refs.dataTable.$el.querySelector('.el-table__footer-wrapper');
      const trow = tbody.querySelector('.el-table__row td');
      let bodyHeight = tbody.offsetHeight;

      if (this.showTotal && ttotal.offsetHeight === 0) {
        bodyHeight -= 54;
      }
      if (this.showHeader && theader.offsetHeight === 0) {
        bodyHeight -= 36;
      }
      const rows = Math.floor(bodyHeight / (trow ? trow.offsetHeight : 32));

      if (rows > 10 && rows !== this.rowsCount) {
        this.$emit('change-rows-count', rows);
        this.tableStatus.rows = true;
      }
    },
    handleSort({ prop, order }, id) {
      if (this.customSorting) {
        if (this.lastSortProp === null) {
          this.lastSortProp = prop;
          this.lastSortOrder = order;
        }

        if (this.lastSortProp === prop) {
          this.sortProp = prop;
          this.sortOrder = order;
          const e = document.getElementsByClassName('el-table__header')[0];
          const removeClass = e.getElementsByTagName('th');

          for (let i = 0; i < removeClass.length; i += 1) {
            removeClass[i].classList.remove('asc', 'desc');
          }

          this.lastSortProp = prop;
          this.lastSortOrder = order;
        } else {
          this.sortProp = prop;
          this.sortOrder = this.lastSortOrder;
          this.$emit('sort', {
            prop: this.sortProp,
            order: this.lastSortOrder,
          });
          const e = document.getElementsByClassName('el-table__header')[0];
          const removeClass = e.getElementsByTagName('th');

          for (let i = 0; i < removeClass.length; i += 1) {
            removeClass[i].classList.remove('asc', 'desc');
          }

          this.lastSortProp = prop;
        }

        const addClass = document.getElementsByClassName(id)[0];
        if (addClass) {
          addClass.classList.add(this.sortOrder);
        }
      } else {
        if (this.lastOpenFilter) {
          this.columnFilters[this.lastOpenFilter].visible = false;
          this.lastOpenFilter = '';
        }

        if (prop && order) {
          if (prop !== this.sortProp || order.substring(0, order.length - 6) !== this.sortOrder) {
            this.sortProp = prop;
            this.sortOrder = order.substring(0, order.length - 6);
            this.lastSortProp = prop;
            this.lastSortOrder = order;
          } else {
            return;
          }
        } else {
          this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
          this.$refs.dataTable.sort(this.sortProp, `${this.sortOrder}ending`);
          this.$refs.dataTable.doLayout();
          this.$nextTick(() => {
            this.$refs.dataTable.sort(this.sortProp, `${this.sortOrder}ending`);
            this.$refs.dataTable.doLayout();
          });
        }

        this.$emit('sort', {
          prop: this.sortProp,
          order: this.sortOrder,
        });
      }
    },
    getSummaryRow(param) {
      if (this.customTotalRow && this.customTotalRow.length > 0) {
        return this.customTotalRow;
      }
      const { columns } = param;
      const sums = [];
      columns.forEach((column, index) => {
        switch (index) {
          case 0: {
            sums[index] = this.$t('dashboard.total');
            break;
          }
          default: {
            sums[index] = '';
            const field = this.fields.find((el) => el.name === column.property);

            if (field) {
              sums[index] = field.format
                ? this.formatValue(
                  this.total[field.name],
                  field.format,
                  field.formatOptions,
                )
                : this.total[field.name];
            }
            break;
          }
        }
      });
      return sums;
    },
    renderHeader({ column, $index }) {
      const UiButton = resolveComponent('ui-button');
      const ElPopover = resolveComponent('el-popover');
      const ElInput = resolveComponent('el-input');
      const ElRadioGroup = resolveComponent('el-radio-group');
      const ElRadioButton = resolveComponent('el-radio-button');

      const { filterable, format } = this.fields.find((el) => el.name === column.property) || '';
      const isCurrencyField = ['currency', 'ncurrency'].includes(format);

      const _self = this;

      return h('div', {
        class: {
          'custom-header': true,
          [column.property]: true,
          'filterable-none': !filterable,
          'header-tooltip': this.$te(`${this.i18nPath}.fields.${column.property}.tooltip`),
        },
        onClick() {
          if (filterable) {
            if (_self.lastOpenFilter) {
              _self.columnFilters[_self.lastOpenFilter].visible = false;
              _self.lastOpenFilter = '';
            }
          }
          if (column.sortable) {
            let order = _self.sortOrder === 'asc' ? 'desc' : 'asc';
            if (_self.lastSortProp !== column.property) {
              order = _self.lastSortOrder;
            }
          }
        },
        
      }, [
        h('div', { class: 'text' }, [
          h('span', {
            class: 'title',
          }, `${column.label}${isCurrencyField && this.currency ? `, ${this.currency}` : ''}`),
        ]),
        // сортинг ниже не используется - проверить(используется дефолтный из компонента)
        _self.customSorting
          ? h(
            'span',
            {
              class: 'caret-wrapper',
              slot: 'reference',
              on: {
                click(event) {
                },
              },
            },
            [
              h('i', {
                name: 'caret',
                size: 10,
                color: '#c0c4cc',
                class: 'sort-caret ascending',
              }),
              h('i', {
                name: 'caret',
                size: 10,
                color: '#c0c4cc',
                style: { marginTop: '-2px' },
                class: 'sort-caret descending',
              }),
            ],
          )
          : '',


        filterable && this.isFiltersInit ? h(
          ElPopover,
          {
            class: 'col-filter',
            trigger: 'click',
            visible: this.columnFilters[column.property].visible,
            width: 'auto',
          },
          {
            default: () => {
              return h(
                'div',
                {
                  class: 'col-filter-wrapper',
                },
                [h(
                  ElRadioGroup,
                  {
                    class: 'tw-mb-8',
                    modelValue: this.columnFilters[column.property].op,
                    'onUpdate:modelValue'(event) {
                      _self.columnFilters[column.property].op = event;
                    },
                  },
                  () => [h(
                    ElRadioButton,
                    {
                      value: '<',
                    },
                    () => '<',
                  ), h(
                    ElRadioButton,
                    {
                      value: '=',
                    },
                    () => '=',
                  ), h(
                    ElRadioButton,
                    {
                      value: '>',
                    },
                    () => '>',
                  )],
                ),
                h(
                  ElInput,
                  {
                    class: `filter-input filter-input-${$index}`,
                    width: 144,
                    modelValue: this.columnFilters[column.property].value,
                    'onUpdate:modelValue'(e) {
                      let value = e;
                      const columns = _self.allMetrics.find((e) => e.name === column.property);

                      if (columns.format === 'ncurrency' || columns.format === 'number' || columns.format === 'percent') {
                        if (value.trim() !== '') {
                          value = value.replace(/((?![0-9,.]).)*/g, '')
                            .replace(/,/g, '.');

                          if (value.includes('.')) {
                            const strArray = value.split('.');
                            value = `${(+strArray.slice(0, strArray.length - 1)
                              .join('')) || '0'}.${strArray.slice(strArray.length - 1)[0].slice(0, 2)}`;
                          } else {
                            value = `${+value}`;
                          }

                          e = value;
                        }
                      }

                      _self.columnFilters = {
                        ..._self.columnFilters,
                        [column.property]: {
                          ..._self.columnFilters[column.property],
                          value,
                        },
                      };
                    },
                  },
                ),
                // не удалять
                // : h(
                //   'el-date-picker',
                //   {
                //     class: 'filter-input datepicker-input',
                //     props: {
                //       width: 144,
                //       value: this.columnFilters[column.property].value,
                //       type: "date",
                //       format: "dd MMM yyyy"
                //     },
                //     on: {
                //       input(event) {
                //         _self.columnFilters[column.property].value = event;
                //       },
                //     },
                //   },
                // ),
                h(
                  'div',
                  {
                    class: 'btns',
                  },
                  [h(
                    UiButton,
                    {
                      color: 'red',
                      onClick() {
                        // if (_self.columnFilters[column.property] !== 'referred_created_at') {
                        _self.columnFilters[column.property].op = '';
                        _self.columnFilters[column.property].value = '';
                        _self.columnFilters[column.property].visible = false;
                        _self.lastOpenFilter = '';
                        _self.checkApplyFilter = true;
                        _self.$nextTick(() => {
                          _self.checkApplyFilter = false;
                        });
                        _self.$emit('filter', _self.columnFilters);
                        // } else {
                        //   _self.lastOpenFilter = '';
                        //   _self.checkApplyFilter = true;
                        //   _self.$nextTick(() => {
                        //     _self.checkApplyFilter = false;
                        //   });
                        //   _self.$emit('filters', _self.columnFilters);
                        // }
                      },
                    },
                    // this.$t('dashboard.reset'),
                    () => [h(
                      'i',
                      {
                        class: 'far fa-times',
                      },
                    ), h(
                      'span',
                      {},
                      this.$t('dashboard.reset'),
                    )],
                  ), h(
                    UiButton,
                    {
                      filled: true,
                      disabled: !(this.columnFilters[column.property].value && this.columnFilters[column.property].op),
                      onClick() {
                        _self.columnFilters[column.property].visible = false;
                        _self.lastOpenFilter = '';
                        _self.$emit('filter', _self.columnFilters);
                        _self.checkApplyFilter = true;
                        _self.$nextTick(() => {
                          _self.checkApplyFilter = false;
                        });
                      },
                    },
                    () => [h(
                      'i',
                      {
                        class: 'far fa-check',
                      },
                    ), h(
                      'span',
                      {},
                      this.$t('alerts.dataTable.apply'),
                    )],
                  )],
                )],
              );
            },
            reference: () => {
              return h(
                'div',
                {
                  class: {
                    'filter-opener filter-opener-active': true,
                    open: (this.lastOpenFilter === column.property
                      || (this.columnFilters[column.property].value && this.columnFilters[column.property].op)),
                  },
                  onClick(event) {
                    event.stopPropagation(true);
                    event.target.closest('.cell')
                      .classList
                      .add('hover-once');
                    const set = !_self.columnFilters[column.property].visible;
                    _self.columnFilters[column.property].visible = set;
                    if (set) {
                      if (_self.lastOpenFilter) {
                        _self.columnFilters[_self.lastOpenFilter].visible = false;
                      }
                      _self.lastOpenFilter = column.property;
                    } else {
                      _self.lastOpenFilter = '';
                    }
                  },
                },
                [h(
                  'i',
                  {
                    name: 'filter',
                    size: 8,
                    class: {
                      'fas fa-filter': true,
                    },
                  },
                )],
              );
            },
          },
        ) : '',


        this.$te(`${this.i18nPath}.fields.${column.property}.tooltip`)
          ? h(
            ElPopover,
            {
              class: 'col-filter',
              trigger: 'hover',
              ref: 'filterWrapper',
            },
            {
              default: () => {
                return h(
                  'div',
                  {
                    class: 'col-filter-wrapper',
                  },
                  this.$t(`${this.i18nPath}.fields.${column.property}.tooltip`),
                );
              },
              reference: () => {
                return h(
                  'div',
                  {
                    class: {
                      'filter-opener tip': true,
                    },
                    onClick(event) {
                      event.stopPropagation(true);
                    },
                  },
                  [h(
                    'i',
                    {
                      name: 'info',
                      size: 8,
                      class: {
                        'fas fa-question': true,
                      },
                    },
                  )],
                );
              },
            },
            
          ) : '',
      ]);
    },
    setSortVisibility(columns) {
      Array.from(document.querySelectorAll('.cell.hover-once'))
        .map((e) => e.classList.remove('hover-once'));
      Array.from(document.querySelectorAll('.cell.hover'))
        .map((e) => e.classList.remove('hover'));
      columns.map((col) => {
        if (col) {
          const header = document.querySelector(`.custom-header.${col}`);
          if (header) {
            const cell = header.closest('.cell');
            if (cell) {
              cell.classList.add('hover');
            }
          }
        }
      });
    },
    formatValue(value, format, options, row) {
      if (value === null) {
        return '—';
      }
      if (!value && value !== 0) {
        return '';
      }
      switch (format) {
        case 'device':
          return 'hello';
        case 'date': {
          const m = value._isAMomentObject
            ? value
            : this.$moment(
              value,
              !this.$_.isEmpty(options) && options.in ? options.in : '',
            );
          return m.isValid()
            ? m.locale(this.$i18n.locale)
              .format(!this.$_.isEmpty(options) && options.out
                ? options.out
                : 'DD.MM.YYYY HH:mm')
            : value;
        }
        case 'format-date': {
          const moment = this.$moment(value);
          return moment.isValid() ? moment.locale(this.$i18n.locale)
            .format('DD MMM YYYY') : '-';
        }
        case 'date-time': {
          const moment = this.$moment(value);
          return moment.isValid() ? moment.locale(this.$i18n.locale)
            .format('DD MMM YYYY HH:mm') : '-';
        }
        case 'date-time-sec': {
          const moment = this.$moment(value);
          return moment.isValid() ? moment.locale(this.$i18n.locale)
            .format('DD MMM YYYY HH:mm:ss') : '-';
        }
        case 'wallet-date': {
          const moment = this.$moment(value);
          return moment.isValid() ? moment.format('YYYY-MM-DD') : '-';
        }
        case 'number':
          return `${parseFloat(value)
            .toLocaleString('en-US')}`;
        case 'percent':
          return `${parseFloat(value / 100)
            .toLocaleString('en-US', {
              style: 'percent',
              maximumFractionDigits: 2,
            })}`;
        case 'odd':
          return `${parseFloat(value)
            .toLocaleString('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}`;
        case 'currency':
          return `${this.currency}${value < 0 ? '-' : ''}${Math.abs(this.$_.floor(value, 2)).toLocaleString('en-US', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}`;
        case 'ncurrency':
          return `${this.currency}${value < 0 ? '-' : ''}${Math.abs(this.$_.floor(value, 4))
            .toLocaleString('en-US', {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}`;
        case 'ncurrencyUSD':
          return `${value < 0 ? '-' : ''}USD ${Math.abs(this.$_.floor(value, 4))
            .toLocaleString('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}`;
        case '-ncurrency':
          return `${value === 0 ? '' : '-'}$${Math.abs(this.$_.floor(value, 4))
            .toLocaleString('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}`;
        case 'country': {
          const c = this.countries.filter((country) => country.code === value);
          return c ? c[0].name : value;
        }
        case 'group': {
          const g = this.groups.filter((group) => group.id === value);
          return g ? g[0].name : value;
        }
        case 'eventType': {
          if (value === 1) {
            return 'Single';
          } else if (value === 0) {
            return '';
          }
          return `Express-${value}`;
        }
        case 'event': {
          if (!Array.isArray(value)) return value;
          if (value.length === 1) {
            return value[0];
          } else if (value.length > 1) {
            return `Express(${row.events_count}/${row.coefficient})`;
          }
          return value;
        }
        default:
          return value;
      }
    },
    handleExpand(row, expRows) {
      this.$emit('expand-change', row, expRows);
    },
    handleCurrentRowChange(current) {
      this.$emit('current-change', current);
    },
    cleanUpSelection() {
      Array.from(document.querySelectorAll('.cell.hover-once'))
        .map((e) => e.classList.remove('hover-once'));
      Array.from(document.querySelectorAll('.cell.hover'))
        .map((e) => e.classList.remove('hover'));
    },
    contextMenu(row, column, event) {
      if (this.checkRows.length >= 1 && this.checkRows.find((e) => e === row.row_id)) {

      } else if (this.checkRows.length >= 1) {
        this.checkRows = [];
      } else {
        this.checkRows.push(row.row_id);
      }
      this.currentRow = row;

      if (this.customContext) {
        event.preventDefault();
        this.$emit('context', row, event);
      }
    },
    copyToClipboard(value) {
      this.copy(value);
      // const textarea = document.createElement('textarea');
      // textarea.contentEditable = true;
      // textarea.readOnly = true;
      // textarea.style.position = 'absolute';
      // textarea.style.left = '-9999px';
      // document.body.appendChild(textarea);
      // textarea.value = value;
      // textarea.select();
      // textarea.setSelectionRange(0, 99999);
      // document.execCommand('copy');
      // textarea.remove();
      // this.copyDone = true;
      // this.$alert.success(this.$t('profile.notifications.copied_message'));
    },
    getSortOrders(isDesc) {
      return isDesc
        ? ['descending', 'ascending']
        : ['ascending', 'descending'];
    },
  },
  created() {
    if (!this.isStatic) {
      this.debouncedTableHeight = this.$_.debounce(
        this.computeTableHeight.bind(this),
        300,
      );
    }
     
    this.sortProp = this.sort.prop || (this.disableSort ? '' : this.fields[0] ? this.fields[0].name : '');
    this.sortOrder = this.sort.order || (this.disableSort ? '' : 'desc');
    document.addEventListener('click', (e) => {
      if (this.lastOpenFilter) {
        let targetEl = e.target;
        do {
          if (targetEl.classList && targetEl.classList.contains('el-popover')) {
            return;
          }
          targetEl = targetEl.parentNode;
        } while (targetEl);
        this.columnFilters[this.lastOpenFilter].visible = false;
        this.lastOpenFilter = '';
      }
      this.fillEmptySpace();
    });

    window.onmouseup = () => {
      setTimeout(() => {
        this.fillEmptySpace();
      }, 100);
    };

    if (!this.$_.isEmpty(this.colFilters)) {
      this.columnFilters = this.$_.cloneDeep(this.colFilters);
    }
    setTimeout(() => {
      if (this.$refs.dataTable) {
        this.$refs.dataTable.debouncedUpdateLayout();
      }
    }, 3000);
  },
  mounted() {
    if (this.rowCheck) {
      document.addEventListener('click', this.pressCtrl, true);
      document.addEventListener('keydown', this.switchMultiSelect, true);
      document.addEventListener('keyup', this.switchMultiSelect, true);
    }

    if (!this.isStatic) {
      this.computeTableHeight();
      window.addEventListener('resize', this.debouncedTableHeight);
      this.$eventBus.$on('connection-enabled', this.debouncedTableHeight);
      this.$watch('tableHeight', (nv, ov) => {
        if (nv !== ov) {
          this.computeRowsCount();
        }
        this.fillEmptySpace();
      });
    }
    this.$emit('init');

    setTimeout(() => {
      this.fillEmptySpace();
    });

    const wrapperContent = document.querySelector('.wrapper .content');
    const wrapperContentPaddingBottom = window
      .getComputedStyle(wrapperContent, null)
      .getPropertyValue('padding-bottom')
      .replace(/[^\d]/g, '');
    const elementPaddingBottom = window
      .getComputedStyle(this.$el, null)
      .getPropertyValue('padding-bottom')
      .replace(/[^\d]/g, '');

    this.pPadding = +wrapperContentPaddingBottom + +elementPaddingBottom;
  },
  activated() {
    if (!this.isStatic) {
      this.debouncedTableHeight();
    }
    this.clearTooltips();
  },
  beforeUnmount() {
    if (!this.isStatic) {
      window.removeEventListener('resize', this.debouncedTableHeight);
    }
    window.onmouseup = null;
  },
  unmounted() {
    document.removeEventListener('click', this.pressCtrl, true);
    document.removeEventListener('keydown', this.switchMultiSelect, true);
    document.removeEventListener('keyup', this.switchMultiSelect, true);
  },
  updated() {
    if (!this.isStatic) {
      this.debouncedTableHeight();
    }
    this.fillEmptySpace();
  },
};
</script>

<style lang="scss">
.ui-table {
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 24px !important;
  .el-popper{
    max-width: 400px;
  }
  &__popper {
    font-size: 11px;
    font-weight: 600;
    line-height: normal;
    border-radius: 8px;
    background-color: rgba(48, 54, 52, 0.9);
    padding: 6px 8px;
  }

  // vue3-migration -- перенёс в UiTableAction.vue
  // .el-tooltip.icon__wrapper {
  //   width: 22px;
  //   height: 22px;
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   border-radius: 50%;
  //   text-decoration: none;

  //   &.text-green:hover {
  //     background: #4d997e30;
  //   }

  //   &.text-orange:hover {
  //     background: rgba(205, 138, 12, .2);
  //   }

  //   &.text-red {
  //     &.disabled {
  //       opacity: 0.4;
  //       pointer-events: none;
  //     }

  //     &:hover {
  //       background: #ff7d8d33;
  //     }
  //   }

  //   &.text-info:hover {
  //     background: #3b94e336;
  //   }

  //   &.disabled {
  //     opacity: .5;
  //     pointer-events: none;
  //   }
  // }

  thead .cell {
    font-size: 12px;
    font-weight: 600;

    & > .custom-header {
      overflow: hidden;
      text-overflow: ellipsis;

      & > .text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    .filterable-none + .caret-wrapper {
      right: 0px !important;
    }
  }

  tbody {
    .el-table__row .divider {
      display: none !important;
    }

    .el-table__cell .cell {
      max-width: 100%;
      line-height: normal;
    }
  }

  &.extend-height {
    .el-table {
      height: 100% !important;
      max-height: 100% !important;

      .el-table__body-wrapper {
        height: 100% !important;
      }
    }
  }

  &.table-actions {
    .el-table__row td:last-child {
      padding: 0px;
    }
  }

  &.fit-content {
    .el-table {
      height: fit-content;
      max-height: fit-content;
    }
  }

  .row-value.actions {
    /*span,*/ a {
      cursor: pointer;
    }
  }

  .fa-filter:before {
    content: "\f0b0";
  }

  @media (max-width: 650px) {
    display: none;
  }

  .el-table.has-footer.el-table--fluid-height tr:last-child td.el-table__cell,
  .el-table.has-footer.el-table--scrollable-y tr:last-child td.el-table__cell {
    border-bottom-color: var(--el-border-color);
  }

  &__fill-empty {
    td {
      padding: 0 !important;
      height: auto;
    }
  }

  .el-tooltip__trigger {
    overflow: hidden;
  }

  .name-bubble {
    text-overflow: ellipsis;
    overflow: hidden;
  }
  //.with-badge {
  //
  //}
}

.el-table .el-table__body-wrapper, .toolbar.tabs .inner {
  &::-webkit-scrollbar-button {
    background-image: unset !important;
    background-repeat: unset !important;
  }

  &::-webkit-scrollbar-track {
    background-color: #f7f7f7 !important;
  }

  &::-webkit-scrollbar-thumb {
    -webkit-border-radius: unset !important;
    border-radius: unset !important;
  }

  &::-webkit-resizer {
    background-repeat: no-repeat;
    width: 4px;
    height: 0;
  }

  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
}

.input-element {
  display: flex;
  align-items: center;
  height: 100%;
  min-width: 10%;
  max-width: 100%;
  flex: 10 1 auto;
  box-sizing: border-box;
  border-radius: 8px !important;
  border: 1px solid #d9d9d9 !important;
  outline: none;
  font-size: 13px;
  padding: 8px 10px;
  background: transparent;

  &:focus{
    border: 1px solid var(--active-color) !important;
  }
}
</style>
